import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { appSignOut } from '@/lib/auth';
import { useHubAccess } from '@/hooks/useHubAccess';
import { useResolvedMediaUrl } from '@/lib/mediaUrl';

export function AccountMenu({ className = '' }: { className?: string }) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const hub = useHubAccess();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const avatarSrc = useResolvedMediaUrl(user?.avatarUrl);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const signOut = async () => {
    setOpen(false);
    await appSignOut();
    setUser(null);
  };

  if (!user) {
    return (
      <Link
        to="/login"
        className={`hidden items-center gap-1.5 rounded-lg px-2 py-2 text-white/80 transition hover:bg-white/10 hover:text-white md:flex ${className}`}
      >
        Sign in
      </Link>
    );
  }

  const firstName = user.displayName?.split(' ')[0] ?? 'Account';
  const showHub = hub.ready && hub.hasAnyHub;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hidden items-center gap-1.5 rounded-lg px-2 py-2 text-white/80 transition hover:bg-white/10 hover:text-white md:flex"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {avatarSrc ? (
          <img src={avatarSrc} alt="" className="h-7 w-7 rounded-full object-cover ring-1 ring-white/30" />
        ) : (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-[10px] font-bold text-white">
            {(user.displayName ?? 'U').slice(0, 2).toUpperCase()}
          </span>
        )}
        <span className="text-xs font-medium">Hello, {firstName}</span>
        <svg className="h-4 w-4 opacity-70" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-[60] mt-1 w-72 rounded-lg border border-slate-200 bg-white py-2 shadow-xl dark:border-slate-700 dark:bg-slate-900">
          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <p className="font-semibold text-slate-900 dark:text-white">{user.displayName}</p>
            <p className="truncate text-xs text-slate-500">{user.email}</p>
          </div>

          <div className="py-1">
            <p className="px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Your account</p>
            <MenuLink to="/orders" onClick={() => setOpen(false)}>Your orders</MenuLink>
            <MenuLink to="/wishlist" onClick={() => setOpen(false)}>Wishlist</MenuLink>
            <MenuLink to="/saved-cards" onClick={() => setOpen(false)}>Saved cards</MenuLink>
            <MenuLink to="/loyalty" onClick={() => setOpen(false)}>Loyalty points</MenuLink>
            <MenuLink to="/gift-cards" onClick={() => setOpen(false)}>Gift cards & certificates</MenuLink>
            <MenuLink to="/referral" onClick={() => setOpen(false)}>Refer a friend</MenuLink>
            <MenuLink to="/messages" onClick={() => setOpen(false)}>Messages</MenuLink>
            <MenuLink to="/account" onClick={() => setOpen(false)}>Account settings</MenuLink>
            <MenuLink to="/notification-prefs" onClick={() => setOpen(false)}>Notifications</MenuLink>
          </div>

          <div className="border-t border-slate-100 py-1 dark:border-slate-800">
            <p className="px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Business</p>
            {showHub ? (
              <MenuLink to={hub.defaultPath} onClick={() => setOpen(false)} bold>
                Business Center
              </MenuLink>
            ) : null}
            {!hub.hasAdminHub && !hub.hasVendorHub ? (
              <MenuLink to="/join?intent=sell" onClick={() => setOpen(false)}>Start selling</MenuLink>
            ) : null}
            {!hub.hasAdminHub && !hub.hasProHub ? (
              <MenuLink to="/join?intent=pro" onClick={() => setOpen(false)}>Become a pro</MenuLink>
            ) : null}
          </div>

          {hub.hasAdminHub ? (
            <div className="border-t border-slate-100 py-1 dark:border-slate-800">
              <MenuLink to="/hub/admin" onClick={() => setOpen(false)}>Admin console</MenuLink>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-1 dark:border-slate-800">
            <button
              type="button"
              onClick={() => void signOut()}
              className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MenuLink({
  to,
  children,
  onClick,
  bold,
}: {
  to: string;
  children: React.ReactNode;
  onClick: () => void;
  bold?: boolean;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800 ${bold ? 'font-semibold text-brand-700 dark:text-brand-400' : ''}`}
    >
      {children}
    </Link>
  );
}

/** Mobile full-screen account panel */
export function MobileAccountPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const hub = useHubAccess();

  if (!open) return null;

  const signOut = async () => {
    onClose();
    await appSignOut();
    setUser(null);
  };

  if (!user) {
    return (
      <div className="fixed inset-0 z-[70] md:hidden">
        <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close" onClick={onClose} />
        <div className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white p-6 dark:bg-slate-900">
          <Link to="/login" onClick={onClose} className="block w-full rounded-lg bg-brand-700 py-3 text-center text-sm font-bold text-white">
            Sign in
          </Link>
          <Link to="/register" onClick={onClose} className="mt-2 block text-center text-sm font-semibold text-brand-700">
            Create account
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] md:hidden">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-4 dark:bg-slate-900">
        <p className="font-bold text-slate-900 dark:text-white">{user.displayName}</p>
        <p className="text-xs text-slate-500">{user.email}</p>
        <nav className="mt-4 space-y-1">
          <MobileLink to="/orders" onClose={onClose}>Your orders</MobileLink>
          <MobileLink to="/grenada/deals" onClose={onClose}>Deals</MobileLink>
          <MobileLink to="/loyalty" onClose={onClose}>Loyalty points</MobileLink>
          <MobileLink to="/gift-cards" onClose={onClose}>Gift cards & certificates</MobileLink>
          <MobileLink to="/account" onClose={onClose}>Account settings</MobileLink>
          {hub.ready && hub.hasAnyHub ? (
            <MobileLink to={hub.defaultPath} onClose={onClose} bold>Business Center</MobileLink>
          ) : null}
          {!hub.hasAdminHub && !hub.hasVendorHub ? (
            <MobileLink to="/join?intent=sell" onClose={onClose}>Start selling</MobileLink>
          ) : null}
          {!hub.hasAdminHub && !hub.hasProHub ? (
            <MobileLink to="/join?intent=pro" onClose={onClose}>Become a pro</MobileLink>
          ) : null}
        </nav>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-4 w-full rounded-lg border border-slate-200 py-2.5 text-sm font-semibold dark:border-slate-700"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function MobileLink({ to, children, onClose, bold }: { to: string; children: React.ReactNode; onClose: () => void; bold?: boolean }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className={`block rounded-lg px-3 py-2.5 text-sm ${bold ? 'font-bold text-brand-700' : 'text-slate-700 dark:text-slate-200'}`}
    >
      {children}
    </Link>
  );
}
